import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { Button } from './ui/button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if user previously dismissed
    const prev = localStorage.getItem('pwa-install-dismissed');
    if (prev) {
      const ts = parseInt(prev, 10);
      // Show again after 7 days
      if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
        return;
      }
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (isInstalled || dismissed || !deferredPrompt) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 flex items-center gap-3 rounded-xl border border-violet-500/30 bg-[#1e1b2e]/95 px-4 py-3 shadow-2xl shadow-violet-500/10 backdrop-blur-lg"
      data-testid="pwa-install-banner"
    >
      <Download className="h-5 w-5 shrink-0 text-violet-400" />
      <span className="text-sm text-violet-100">
        Install <strong>LabFlo</strong> for quick access
      </span>
      <Button
        size="sm"
        onClick={handleInstall}
        className="bg-violet-600 hover:bg-violet-500 text-white text-xs h-8 px-3"
        data-testid="pwa-install-btn"
      >
        Install
      </Button>
      <button
        onClick={handleDismiss}
        className="text-violet-300/50 hover:text-violet-200 transition-colors"
        data-testid="pwa-install-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
